'use client';

import { ErrorOverlay } from '@/app/components/ErrorOverlay';

import React, { createContext, ReactNode, useContext, useState } from 'react';

interface ErrorState {
  hasError: boolean;
  error: Error | null;
  message?: string;
  isBlocking: boolean;
  retryAction?: () => void;
  goBackAction?: () => void;
}

interface ErrorBoundaryContextType {
  errorState: ErrorState;
  showError: (
    error: Error,
    message?: string,
    retryAction?: () => void,
    goBackAction?: () => void
  ) => void;
  hideError: () => void;
  retry: () => void;
  isRetrying: boolean;
  clearErrorState: () => void;
}

const ErrorBoundaryContext = createContext<ErrorBoundaryContextType | undefined>(undefined);

interface ErrorBoundaryProviderProps {
  children: ReactNode;
  onRetry?: () => void;
}

/**
 * Proveedor del contexto de manejo global de errores. Proporciona funcionalidad para mostrar, ocultar y reintentar errores en toda la aplicación.
 *
 * @param {ErrorBoundaryProviderProps} props - Las propiedades del proveedor.
 *
 * @returns {JSX.Element} El proveedor del contexto.
 */
export const ErrorBoundaryProvider: React.FC<ErrorBoundaryProviderProps> = ({
  children,
  onRetry
}) => {
  const [errorState, setErrorState] = useState<ErrorState>({
    hasError: false,
    error: null,
    isBlocking: false
  });
  const [isRetrying, setIsRetrying] = useState(false);

  const showError = (
    error: Error,
    message?: string,
    retryAction?: () => void,
    goBackAction?: () => void
  ) => {
    setErrorState({
      hasError: true,
      error,
      message,
      isBlocking: true,
      retryAction,
      goBackAction
    });
  };

  const hideError = () => {
    setErrorState({
      hasError: false,
      error: null,
      isBlocking: false,
      retryAction: undefined,
      goBackAction: undefined
    });
  };

  const retry = async () => {
    setIsRetrying(true);

    try {
      if (errorState.retryAction) {
        hideError();
        await errorState.retryAction();
        setIsRetrying(false);
      } else if (onRetry) {
        hideError();
        onRetry();
        setIsRetrying(false);
      } else {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        hideError();
        window.location.replace(window.location.href);
      }
    } catch (error) {
      showError(error instanceof Error ? error : new Error(String(error)));
      setIsRetrying(false);
    }
  };

  const clearErrorState = () => {
    hideError();
  };

  const handleGoBack = () => {
    hideError();
    if (errorState.goBackAction) {
      errorState.goBackAction();
    } else if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = '/';
    }
  };

  return (
    <ErrorBoundaryContext.Provider
      value={{ errorState, showError, hideError, retry, isRetrying, clearErrorState }}>
      {children}

      {errorState.hasError && (
        <>
          <ErrorOverlay
            text={errorState.message}
            primaryCallback={async () => {
              try {
                await retry();
              } catch (error) {
                console.error('Error al ejecutar retry:', error);
              }
            }}
            secondaryCallback={handleGoBack}
          />
        </>
      )}
    </ErrorBoundaryContext.Provider>
  );
};

/**
 * Hook personalizado para acceder al contexto de manejo de errores. Debe ser usado dentro de un ErrorBoundaryProvider.
 *
 * @returns {ErrorBoundaryContextType} El contexto de manejo de errores.
 *
 * @throws {Error} Si se usa fuera de un ErrorBoundaryProvider.
 */
export const useErrorBoundary = (): ErrorBoundaryContextType => {
  const context = useContext(ErrorBoundaryContext);
  if (!context) {
    throw new Error('useErrorBoundary must be used within an ErrorBoundaryProvider');
  }
  return context;
};
